/* eslint-disable react/no-unused-prop-types */
import BuildShowStore from "~/stores/BuildShowStore";
import { Build } from "~/components/build/Show/lib/types";
import {
  BuildContextProvider,
  LegacyBuildContextProvider,
  useBuild,
} from "~/components/build/Show/lib/BuildContext";
import {
  RouterProvider,
  Navigate,
  RouteObject,
  useNavigate,
  useLocation,
  useRouteError,
  isRouteErrorResponse,
} from "react-router-dom";

// Use react-router with support for Datadog RUM (when enabled)
// @see https://docs.datadoghq.com/integrations/rum-react/#react-router-integration
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";

import Database from "~/lib/Database";
import { ErrorView } from "~/components/shared/ErrorView";
import BuildShowLayout from "~/components/build/Show/layouts/BuildShowLayout";
import JobsPage from "~/components/build/Show/routes";
import SummaryPage from "~/components/build/Show/layouts/SummaryLayout";
import AnnotationsPage from "~/components/build/Show/routes/annotations";
import TestsPage from "~/components/build/Show/routes/tests";
import Overview from "~/components/build/Show/routes/summary";
import { StrictMode, useEffect } from "react";
import Bugsnag from "~/lib/Bugsnag";
import { type FaviconPaths } from "~/lib/Favicon";
import { useFaviconPathsStore } from "~/components/build/Show/lib/useFaviconPathsStore";
import { useWaterfallAvailableStore } from "~/components/build/Show/lib/useWaterfallAvailableStore";
import StepUploads from "~/components/build/Show/routes/summary/step-uploads";
import {
  urlForView,
  convertClassicToModernParams,
  convertModernToClassicParams,
} from "~/components/build/Show/lib/urlForView";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";

import StepsLayout from "~/components/build/Show/layouts/StepsLayout";
import WaterfallPage from "~/components/build/Show/routes/waterfall";
import TablePage from "~/components/build/Show/routes/table";
import CanvasPage from "~/components/build/Show/routes/canvas";
import ListPage from "~/components/build/Show/routes/list";

import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import initDatadogRum, {
  DatadogRumInitConfiguration,
} from "~/components/build/Show/lib/initDatadogRum";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useWindowFocusRefetch } from "~/components/build/Show/lib/useWindowFocusRefetch";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { FilterByOption, useFilterStore } from "~/components/build/Show/lib/useFilterStore";
import { HotkeysProvider } from "react-hotkeys-hook";

/**
 * Redirect to a BuildView, consistently converting search params and hash
 * between classic and modern (BBUI) URL formats.
 *
 * In BBUI mode, classic hash fragments (e.g. `#job-uuid/22-25`) are converted
 * to modern query params (`?jid=job-uuid`) with a simplified hash.
 * In Classic mode, modern query params are converted back to hash fragments.
 */
export function BuildViewRedirect({ to }: { to: BuildView }) {
  const { build } = useBuild();
  const location = useLocation();

  const { search, hash } = Features.BuildSidebar
    ? convertClassicToModernParams(location.search, location.hash)
    : convertModernToClassicParams(location.search, location.hash);

  return <Navigate to={`${urlForView(build.path, to)}${search}${hash}`} replace={true} />;
}

/**
 * Resolve the user's default view preference, then redirect via BuildViewRedirect.
 */
function useDefaultView(): BuildView {
  const { build } = useBuild();
  const pipeline = build.project.slug;

  const getDefaultView = useBuildPreferencesStore((state) => state.getDefaultView);
  const defaultView = getDefaultView(pipeline || "");

  if (Features.DefaultToAnnotationsTab) {
    return BuildView.Annotations;
  }

  if (defaultView === BuildView.Tests && !build.testEngine.testTabUrls?.tabUrl) {
    return BuildView.Summary;
  }

  return defaultView;
}

function BaseRouteRedirect() {
  const to = useDefaultView();
  return <BuildViewRedirect to={to} />;
}

function StepUploadsRedirect() {
  const { build } = useBuild();
  const redirectPath = `${build.path}/step-uploads`;

  // Step uploads live on a server-side route for the classic page so we need to do a full
  // redirect
  useEffect(() => {
    window.location.href = redirectPath;
  }, [redirectPath]);

  return null;
}

export const routes: RouteObject[] = Features.BuildSidebar
  ? [
      {
        path: ":org/:pipeline/builds/:number/",
        element: <BuildShowLayout />,
        errorElement: <RouteError />,
        children: [
          {
            id: BuildView.Index,
            index: true,
            element: <BaseRouteRedirect />,
            errorElement: <RouteError />,
          },
          {
            path: "summary",
            element: <SummaryPage />,
            errorElement: <RouteError />,
            children: [
              {
                id: BuildView.Summary,
                index: true,
                element: <Overview />,
                errorElement: <RouteError />,
              },
              {
                id: BuildView.StepUploads,
                path: "step-uploads",
                element: <StepUploads />,
                errorElement: <RouteError />,
              },
            ],
          },
          {
            element: <StepsLayout />,
            errorElement: <RouteError />,
            children: [
              {
                path: "table",
                id: BuildView.Table,
                element: <TablePage />,
                errorElement: <RouteError />,
              },
              {
                path: "canvas",
                id: BuildView.Canvas,
                element: <CanvasPage />,
                errorElement: <RouteError />,
              },
              ...(Features.BuildPageListView
                ? [
                    {
                      path: "list",
                      id: BuildView.List,
                      element: <ListPage />,
                      errorElement: <RouteError />,
                    },
                  ]
                : []),
              {
                path: "waterfall",
                id: BuildView.Waterfall,
                element: <WaterfallPage allowCollapse={true} />,
                errorElement: <RouteError />,
              },
            ],
          },
          ...(!Features.BuildPageDockedAnnotations
            ? [
                {
                  path: "annotations",
                  id: BuildView.Annotations,
                  element: <AnnotationsPage />,
                  errorElement: <RouteError />,
                },
              ]
            : []),
          {
            path: "tests",
            id: BuildView.Tests,
            element: <TestsPage />,
            errorElement: <RouteError />,
          },
          // Legacy /steps/* redirects
          { path: "steps/canvas", element: <BuildViewRedirect to={BuildView.Canvas} /> },
          { path: "steps/table", element: <BuildViewRedirect to={BuildView.Table} /> },
          { path: "steps/waterfall", element: <BuildViewRedirect to={BuildView.Waterfall} /> },
          { path: "steps/list", element: <BuildViewRedirect to={BuildView.List} /> },
          {
            path: "*",
            element: <BuildViewRedirect to={BuildView.Index} />,
            errorElement: <RouteError />,
          },
        ],
      },
    ]
  : [
      {
        path: ":org/:pipeline/builds/:number/",
        element: <BuildShowLayout />,
        errorElement: <RouteError />,
        children: [
          {
            index: true,
            id: BuildView.Index,
            element: <JobsPage />,
            errorElement: <RouteError />,
          },
          {
            path: "canvas",
            id: BuildView.Canvas,
            element: <CanvasPage />,
            errorElement: <RouteError />,
          },
          {
            path: "waterfall",
            id: BuildView.Waterfall,
            element: <WaterfallPage allowCollapse={false} />,
            errorElement: <RouteError />,
          },
          {
            path: "tests",
            id: BuildView.Tests,
            element: <TestsPage />,
            errorElement: <RouteError />,
          },
          {
            path: "summary/step-uploads",
            element: <StepUploadsRedirect />,
            errorElement: <RouteError />,
          },
          // Legacy /steps/* redirects
          { path: "steps/canvas", element: <BuildViewRedirect to={BuildView.Canvas} /> },
          { path: "steps/table", element: <BuildViewRedirect to={BuildView.Table} /> },
          { path: "steps/waterfall", element: <BuildViewRedirect to={BuildView.Waterfall} /> },
          { path: "steps/list", element: <BuildViewRedirect to={BuildView.List} /> },
          {
            path: "*",
            element: <BuildViewRedirect to={BuildView.Index} />,
            errorElement: <RouteError />,
          },
        ],
      },
    ];

function RouteError() {
  const navigate = useNavigate();
  const routeError = useRouteError();

  // A 404 (Not Found) can occur when navigating to a non-existent route that's been pushed into history
  // by another client-side history action. (ie. turbo links)
  const is404 = isRouteErrorResponse(routeError) && routeError.status === 404;

  useEffect(() => {
    // If the error is a 404, don't report it to Bugsnag.
    if (is404) {
      return;
    }

    if (routeError instanceof Error) {
      Bugsnag.notify(routeError, (event) => {
        event.severity = "error";
      });
    }
  }, [routeError, is404]);

  // If the error is a 404, don't render an error message.
  if (is404) {
    return null;
  }

  // eslint-disable-next-line react/jsx-no-bind
  return <ErrorView className="px-4 py-2" clearError={() => navigate(0)} />;
}

interface PageProps {
  build: Build;
  faviconPaths: FaviconPaths;
  waterfallAvailable: boolean;
  datadogRum?: DatadogRumInitConfiguration;
}

/**
 * Adds backwards compatibility for references to job log lines that don't belong to a log
 * group. We now give those lines an id of `0` for the log group portion, but it used to
 * be `undefined`.
 */
function handleUndefinedLogGroups() {
  // modified hash match from `parseHash`
  const match = window.location.hash.match(/([^/#]+)(?:\/?(undefined|\d+)?-?(\d+)?)?/);
  if (match) {
    const [job, group, line] = match.slice(1);
    if (group === `undefined`) {
      const baseUrl = window.location.href.split("#")[0];
      const newHash = `#${job}/0-${line}`;
      window.location.href = `${baseUrl}${newHash}`;
    }
  }
}

// Browsers throttle/suspend WebSocket connections in background tabs, so
// ActionCable events can be silently lost. Stale queries are refetched when
// the user returns to the tab to recover from missed updates.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: true,
    },
  },
});

export default (props: PageProps) => {
  useFaviconPathsStore.getState().setFaviconPaths(props.faviconPaths);
  useWaterfallAvailableStore.getState().setWaterfallAvailable(props.waterfallAvailable);

  const router = createBrowserRouter(routes);

  useWindowFocusRefetch();
  handleUndefinedLogGroups();

  // Instrument the new build experience when DatadogRUM is enabled.
  if (Features.DatadogRUM && Features.BuildSidebar && props.datadogRum) {
    initDatadogRum(props.datadogRum);
  }

  if (!Features.BuildSidebar) {
    const store = new BuildShowStore(props);
    useBuildPageStore.getState().setSteps(store.build.steps, store.build);
    useBuildPageStore.getState().setJobs(store.build.jobs);

    return (
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <LegacyBuildContextProvider store={store}>
            <HotkeysProvider>
              <RouterProvider router={router} />
            </HotkeysProvider>
          </LegacyBuildContextProvider>
        </QueryClientProvider>
      </StrictMode>
    );
  }

  const initialBuild: Build = Database.parse(props.build);

  // Auto-apply "Failed" filter on initial page load when the server indicates the build has
  // failures worth highlighting. Only runs once on bootstrap so the filter doesn't get
  // re-applied as the build refreshes (which would override the user's manual filter changes).
  if (initialBuild.applyFailedFilter) {
    useFilterStore.getState().setFilterBy([FilterByOption.Failed]);
  }

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BuildContextProvider initialBuild={initialBuild}>
          <HotkeysProvider>
            <RouterProvider router={router} />
          </HotkeysProvider>
        </BuildContextProvider>

        {SHOW_REACT_QUERY_DEVTOOLS === "true" && <ReactQueryDevtools />}
      </QueryClientProvider>
    </StrictMode>
  );
};
